import { fract, range } from "math"

const removeDecimals = (x: string | number): string => {
  if (typeof x === 'number') return Math.floor(x) + '';
  if (x.includes('.')) {
    const [left, _right] = x.split('.');
    return left;
  }
  return x + '';
}

const asNumber = (x: any): number => {
  if (typeof x === 'number') return x;
  if (typeof x === 'string') {
    if (x === '' || x.length <= 0 || x === ' ' || x === '\n') return 0;
    let buff: string = '';

    for (let i = 0; i < x.length; i++) {
      const c = x[i];

      if (Array.from('0123456789').includes(c) || c === '.' || c === '-') {
        buff += c;
      }
    }
    return Number(buff);
  }
  return Number(x);
}

const adjustDecimals = (x: number | string, min: number, max: number) => {
  const str = `${x}`;
  const parts = str.includes('.') ? str.split('.') : [str, ''];
  const left = parts[0];
  const right = parts[1];
  let decimals = right.split('');
  if (decimals.length >= min && decimals.length <= max) return str;
  if (decimals.length < min) {
    decimals = [...decimals, ...range(Math.floor(min - decimals.length)).map(() => '0')];
  }
  if (decimals.length > max) {
    decimals = decimals.slice(0, max);
  }
  return `${left}.${decimals.join('')}`
}

const truncateNumber = (n: number, lowPrecision: number = 6, highPrecision: number = 1): string => {
  const thousand = 1000
  const million = 1000000
  const billion = 1000000000
  const trillion = 1000000000000
  if (Math.abs(n) >= trillion) return (n / trillion).toFixed(highPrecision) + ' T'
  if (Math.abs(n) >= billion) return (n / billion).toFixed(highPrecision) + ' B'
  if (Math.abs(n) >= million) return (n / million).toFixed(highPrecision) + ' M'
  if (Math.abs(n) >= thousand) return (n / thousand).toFixed(highPrecision) + ' K'
  return n.toFixed(lowPrecision)
}

export type PrettyNumberOptions = {
  precision?: number;
  lowPrecision?: number;
  highPrecision?: number;
  minDecimals?: number;
  maxDecimals?: number;
  autoDecimals?: boolean;
  integer?: boolean;
  suffix?: string;
  american?: boolean;
  truncate?: boolean;
  zeroIsInteger?: boolean;
}

type PatchFn = ((x: number | string) => number | string);

export const prettyNumber = (x: number, options: PrettyNumberOptions = {}): string => {
  options = {...options};

  const patches: PatchFn[] = [];

  if (options.zeroIsInteger) {
    if (Math.abs(x) <= 0.000000000001 || fract(x) <= 0) {
      options.integer = true;
      options.minDecimals = 0;
      options.maxDecimals = 1;
    }
  }

  if (options.autoDecimals) {
    (() => {
      const f = fract(x);
      if (f <= 0) {
        options.integer = true;
      }

      const dec = `${x}`.includes('.') ? Array.from(`${x}`.split('.')[1]) : [];
      const zeroes = dec.filter(it => it === '0');
      const allZeroes = zeroes.length === dec.length;

      if (allZeroes) return;
      options.minDecimals = zeroes.length + 1;
      options.maxDecimals = Math.max(options.maxDecimals ?? 0, options.minDecimals ?? 0);
    })();
  }

  const precision = options.precision ?? 2;
  const lowPrecision = options.lowPrecision ?? options.minDecimals ?? precision;
  const highPrecision = options.highPrecision ?? options.minDecimals ?? precision;


  if (options.integer) {
    patches.push((x) => removeDecimals(x));
  } else if (precision > 0) {
    patches.push((x) => {
      x = asNumber(x);
      if (precision <= 0) return Math.round(x) + '';
      const fractions = fract(x);
      if (fractions <= 0.000000000001) return Math.round(x) + '';
      const numFractions = `${x}`.includes('.') ? `${x}`.split('.')[1].length : 0;
      if (numFractions <= precision) return x + '';
      return x.toFixed(precision);
    })
  }

  if (options.minDecimals !== undefined || options.maxDecimals !== undefined) {
    patches.push((x) => {
      return adjustDecimals(asNumber(x), options.minDecimals ?? 0, options.maxDecimals ?? precision)
    })
  }



  if (options.american) {
    patches.push((x) => {
      x = asNumber(x);
      let formatted = x.toLocaleString('en-US', { minimumFractionDigits: options.minDecimals ?? 0, maximumFractionDigits: options.maxDecimals ?? 2, ...(options || {}) });
      if (precision <= 0 && formatted.includes('.')) {
        formatted = formatted.split('.')[0];
      }
      return formatted;
    })
  }

  if (options.truncate) {
    patches.push((x) => truncateNumber(asNumber(x), lowPrecision, highPrecision));
  }

  let v: string = `${x}`;

  for (const patch of patches) {
    v = patch(v) + '';
  }

  if (options.suffix) v = `${v} ${options.suffix}`;

  return v + '';
}
